import type { ManagementApiKey } from '@kirin/api-client/contracts/management-api-keys';
import {
  V1_SCOPE_RESOURCES,
  isFullAccess,
  isReadOnly,
  readScopeFor,
  writeScopeFor,
  type V1Scope,
  type V1ScopeResource,
} from '@kirin/api-client/contracts/v1/scopes';

export type ManagementKeyStatus = 'Active' | 'Expired' | 'Revoked';

/**
 * Resolve the display status of a management API key.
 *
 * The expiry check runs **before** the `isActive` check — the inverse of
 * `pluginKeyStatus`. That is deliberate: the management-key list view already
 * demotes `isActive` to `false` for anything past its `expiresAt`, so testing
 * `isActive` first would label every naturally expired key "Revoked" and lose
 * the distinction between "the clock ran out" and "a human killed it".
 *
 * `'Revoked'` is reachable even though the list omits DB-revoked rows
 * (`includeRevoked: false`): the backend's `resolveIsActive` also reports
 * `isActive: false` for a row that is still live locally but that Descope
 * reports as inactive or deleted — a key revoked directly in Descope, or a mint
 * that half-failed. Those are exactly the rows an operator most needs to see
 * flagged rather than silently listed as Active.
 */
export function managementKeyStatus(
  key: Pick<ManagementApiKey, 'expiresAt' | 'isActive'>
): ManagementKeyStatus {
  if (key.expiresAt && new Date(key.expiresAt).getTime() <= Date.now()) {
    return 'Expired';
  }
  if (!key.isActive) return 'Revoked';
  return 'Active';
}

/**
 * End of the picked calendar day, as a UTC instant.
 *
 * `react-day-picker` hands back the clicked day at **local** midnight — it only
 * anchors calendar math to noon when both `noonSafe` and `timeZone` are passed,
 * and `ui/calendar` passes neither. Reading that instant's *UTC* parts, which is
 * what the shared `endOfDayUTC` does, therefore shifts the date back a day for
 * every user east of UTC: at UTC+2, clicking the 20th produced an expiry at the
 * end of the 19th, and a key that died a day before the user asked.
 *
 * So take the day's **local** calendar parts and rebuild the instant in UTC. The
 * contract becomes "expires at the end of the chosen calendar date, UTC" — the
 * same date the user clicked, in every zone.
 *
 * Deliberately *not* a change to `endOfDayUTC` itself: the alert mute and
 * exclude buttons share it, and re-dating suppression windows is not this
 * change's business.
 */
export function endOfPickedDayUtc(day: Date): Date {
  return new Date(
    Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999)
  );
}

/**
 * Local midnight tomorrow — the earliest day the expiry picker allows.
 *
 * A local day, because `react-day-picker` compares its cells in local calendar
 * days. The shared `tomorrowUtcStart` returns tomorrow at *UTC* midnight, which
 * lands on **today** for anyone west of UTC (at UTC-8, tomorrow-UTC-midnight is
 * today 16:00 local), so the picker let those users choose today and only
 * blocked it east of UTC.
 *
 * Every day this permits still yields a future expiry once run through
 * {@link endOfPickedDayUtc}, in any zone from UTC-11 to UTC+14 — which the
 * contract's `.refine(future)` requires.
 */
export function startOfTomorrowLocal(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
}

/**
 * How close a key is to expiring, for the "Expires" column's colour.
 *
 * - `'imminent'` — expires within {@link EXPIRY_IMMINENT_DAYS} days (red)
 * - `'approaching'` — expires within {@link EXPIRY_APPROACHING_DAYS} days but
 *   more than {@link EXPIRY_IMMINENT_DAYS} away (orange)
 * - `'none'` — no expiry, or far enough out to need no emphasis
 */
export type ExpiryUrgency = 'none' | 'approaching' | 'imminent';

/** Inside this many days the expiry reads red. Boundary inclusive. */
export const EXPIRY_IMMINENT_DAYS = 14;

/** Inside this many days the expiry reads orange. Boundary inclusive. */
export const EXPIRY_APPROACHING_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Classify how urgently a key's expiry needs attention.
 *
 * Both thresholds are **inclusive**, so a key exactly 14 days out is
 * `'imminent'` and one exactly 30 days out is `'approaching'` — the reading
 * that matches "within 14 days" / "within 30 days".
 *
 * An **already-expired** key is `'imminent'`, not `'none'`. It is the most
 * urgent state there is, and leaving it unstyled while a key 13 days out shows
 * red would read as a bug. The Status column still distinguishes the two.
 *
 * `now` is injectable so tests do not depend on the wall clock.
 */
export function expiryUrgency(
  expiresAt: Date | string | null,
  now: Date = new Date()
): ExpiryUrgency {
  if (!expiresAt) return 'none';

  const expiryMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiryMs)) return 'none';

  const remainingMs = expiryMs - now.getTime();
  if (remainingMs <= EXPIRY_IMMINENT_DAYS * DAY_MS) return 'imminent';
  if (remainingMs <= EXPIRY_APPROACHING_DAYS * DAY_MS) return 'approaching';
  return 'none';
}

/** Expiration choices offered by the create dialog. */
export type ExpirationPreset =
  | '30_days'
  | '60_days'
  | '90_days'
  | '6_months'
  | '1_year'
  | 'never'
  | 'custom';

/** Display labels, in the order the select renders them. */
export const EXPIRATION_PRESET_LABELS: Readonly<
  Record<ExpirationPreset, string>
> = {
  '30_days': '30 days',
  '60_days': '60 days',
  '90_days': '90 days',
  '6_months': '6 months',
  '1_year': '1 year',
  never: 'Never',
  custom: 'Custom date',
};

export const EXPIRATION_PRESET_ORDER: readonly ExpirationPreset[] = [
  '30_days',
  '60_days',
  '90_days',
  '6_months',
  '1_year',
  'never',
  'custom',
];

/**
 * Pre-selected expiration. A bounded default rather than "Never", so a user who
 * accepts the form as-is still gets a key that eventually dies.
 */
export const DEFAULT_EXPIRATION_PRESET: ExpirationPreset = '90_days';

/** Max name length, mirroring `createManagementApiKeyRequestSchema`. */
export const MANAGEMENT_KEY_NAME_MAX_LENGTH = 100;

/**
 * Resolve a preset into the absolute expiry to send, relative to `from`.
 *
 * Returns `undefined` for `never` (the field is omitted entirely, which is what
 * the contract reads as "no expiry") and for `custom` (the caller supplies the
 * picked date instead).
 *
 * Month-based presets use real calendar math with JS's standard overflow rule
 * (Aug 31 + 6 months → Mar 3), so the label matches what a user expects rather
 * than a 180-day approximation.
 */
export function resolveExpiration(
  preset: ExpirationPreset,
  from: Date = new Date()
): Date | undefined {
  const d = new Date(from.getTime());
  switch (preset) {
    case '30_days':
      d.setUTCDate(d.getUTCDate() + 30);
      return d;
    case '60_days':
      d.setUTCDate(d.getUTCDate() + 60);
      return d;
    case '90_days':
      d.setUTCDate(d.getUTCDate() + 90);
      return d;
    case '6_months':
      d.setUTCMonth(d.getUTCMonth() + 6);
      return d;
    case '1_year':
      d.setUTCFullYear(d.getUTCFullYear() + 1);
      return d;
    case 'never':
    case 'custom':
      return undefined;
  }
}

/**
 * What a key may do with one resource group, as the create dialog models it
 * (KIR-4377).
 *
 * Three states rather than two independent checkboxes, because `write` implies
 * `read` on the server: a "write but not read" tick would be a box the user can
 * set and the system cannot honour. Collapsing the pair into one choice makes
 * the impossible state unrepresentable rather than merely discouraged.
 */
export type ScopeLevel = 'none' | 'read' | 'write';

/** The level chosen for each resource group. */
export type ScopeSelection = Readonly<Record<V1ScopeResource, ScopeLevel>>;

/**
 * Read on everything, write on nothing.
 *
 * The dialog's starting point, and deliberately not "nothing selected": the
 * overwhelmingly common integration — a SIEM export, a dashboard — is
 * read-only, so the default should be the safe answer that most people are
 * about to pick anyway. Starting from nothing would push every user through the
 * matrix on their way to the same place, and a form that must be filled in is a
 * form people fill in carelessly.
 */
export const DEFAULT_SCOPE_SELECTION: ScopeSelection = Object.freeze(
  Object.fromEntries(
    V1_SCOPE_RESOURCES.map(resource => [resource, 'read'])
  ) as Record<V1ScopeResource, ScopeLevel>
);

/** Nothing selected — the starting point for building a narrow key by hand. */
export const EMPTY_SCOPE_SELECTION: ScopeSelection = Object.freeze(
  Object.fromEntries(
    V1_SCOPE_RESOURCES.map(resource => [resource, 'none'])
  ) as Record<V1ScopeResource, ScopeLevel>
);

/**
 * Everything, at the highest level each group offers.
 *
 * A group with no write level gets `read`, which is all it has — asking for a
 * write that does not exist would be rejected by the contract's enum.
 */
export const FULL_SCOPE_SELECTION: ScopeSelection = Object.freeze(
  Object.fromEntries(
    V1_SCOPE_RESOURCES.map(resource => [
      resource,
      writeScopeFor(resource) ? 'write' : 'read',
    ])
  ) as Record<V1ScopeResource, ScopeLevel>
);

/**
 * The scope list to send for a selection.
 *
 * A `write` level sends **only** the write scope, not both: the server treats
 * `X:write` as satisfying `X:read`, so sending both would store a redundant
 * entry that every later reader — the badge, the health response, the token
 * echo — would have to normalise away.
 */
export function scopeSelectionToList(selection: ScopeSelection): V1Scope[] {
  const scopes: V1Scope[] = [];
  for (const resource of V1_SCOPE_RESOURCES) {
    const level = selection[resource];
    if (level === 'read') {
      scopes.push(readScopeFor(resource));
      continue;
    }
    if (level === 'write') {
      // `?? readScopeFor` cannot normally fire: `FULL_SCOPE_SELECTION` never
      // sets `write` on a group without one, and the dialog only offers the
      // level for groups that have it. It is here so a hand-built selection
      // degrades to the strongest scope that exists rather than silently
      // dropping the group.
      scopes.push(writeScopeFor(resource) ?? readScopeFor(resource));
    }
  }
  return scopes;
}

/**
 * Short label for a key's scopes, for the list column.
 *
 * `null` — a key minted before scopes existed — reads as *Full access*, because
 * that is precisely what it has. It is not distinguished from a deliberately
 * unrestricted key: the two are equivalent in authority, and the only useful
 * advice for either is the same.
 */
export function describeScopes(scopes: readonly V1Scope[] | null): string {
  if (scopes === null || isFullAccess(scopes)) return 'Full access';
  if (scopes.length === 0) return 'None';
  if (isReadOnly(scopes)) {
    return scopes.length === V1_SCOPE_RESOURCES.length
      ? 'Read-only'
      : `Read-only (${scopes.length})`;
  }
  return `Custom (${scopes.length})`;
}
